import Link from 'next/link';
import { motion } from 'framer-motion';
import WideLayout from '@components/layout/wide';

type HoldingMessageContent =
  | { html: string; pageTitle?: never; isAppView?: never }
  | { pageTitle: string; isAppView?: boolean; html?: never };

type EndOfSeasonHoldingMessageProps = HoldingMessageContent & {
  heading?: string;
  accentHeading?: string;
  status?: string;
};

const DEFAULT_HEADING = "We're nearly there…";
const DEFAULT_ACCENT_HEADING = "We're updating our data and tools";
const DEFAULT_STATUS = 'Updating FFH Data';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' as const }
  }
};

const EndOfSeasonHoldingMessage = ({
  html,
  pageTitle,
  isAppView,
  heading = DEFAULT_HEADING,
  accentHeading = DEFAULT_ACCENT_HEADING,
  status = DEFAULT_STATUS
}: EndOfSeasonHoldingMessageProps) => {
  return (
    <WideLayout className="bg-white px-3" content={false}>
      <div className="relative mx-auto max-w-5xl px-2 py-8 md:py-14 lg:py-20">
        <motion.section
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="relative overflow-hidden rounded-3xl bg-primary-900 shadow-500"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.06] bg-diagonal-stripes"
          />

          <svg
            aria-hidden
            viewBox="0 0 320 200"
            className="absolute -right-10 -top-10 w-72 md:w-96 text-primary-700/40"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          >
            <rect x="0.5" y="0.5" width="319" height="199" rx="6" />
            <line x1="160" y1="0" x2="160" y2="200" />
            <circle cx="160" cy="100" r="34" />
            <rect x="0" y="60" width="40" height="80" />
            <rect x="280" y="60" width="40" height="80" />
            <circle cx="160" cy="100" r="1.5" fill="currentColor" />
          </svg>

          <div
            aria-hidden
            className="absolute -left-32 -bottom-32 h-80 w-80 rounded-full bg-primary-700/20 blur-3xl"
          />

          <div className="relative z-10 px-6 py-12 md:px-14 md:py-16 lg:px-20 lg:py-20">
            <motion.div
              variants={itemVariants}
              className="flex items-center gap-3"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-400" />
              </span>
              <span className="font-open-sans text-xs font-semibold tracking-[0.3em] text-primary-200">
                FPL IS LIVE · We won&apos;t be long
              </span>
            </motion.div>

            <motion.h2
              variants={itemVariants}
              className="mt-7 font-sans text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tight text-white"
            >
              {heading}
              <span className="mt-2 block text-primary-400">
                {accentHeading}
              </span>
            </motion.h2>

            <motion.div
              variants={itemVariants}
              className="mt-10 h-px w-20 bg-primary-400"
            />

            <motion.div variants={itemVariants} className="mt-8 max-w-2xl">
              {html ? (
                <p
                  className="font-open-sans text-base md:text-lg leading-relaxed text-primary-100/90"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ) : (
                <p className="font-open-sans text-base md:text-lg leading-relaxed text-primary-100/90">
                  {pageTitle} will be here when we&apos;ve finished our updates.
                  In the meantime, take a look at the latest articles{' '}
                  {isAppView ? (
                    'in the content section'
                  ) : (
                    <>
                      on our homepage{' '}
                      <Link
                        href="/"
                        className="font-semibold text-white underline decoration-white decoration-2 underline-offset-4 transition-colors hover:decoration-primary-300"
                      >
                        here
                      </Link>
                    </>
                  )}
                </p>
              )}
            </motion.div>

            <motion.dl variants={itemVariants} className="mt-14">
              <dt className="font-open-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-primary-300">
                Status
              </dt>
              <dd className="mt-2 font-sans text-lg md:text-xl font-bold leading-none text-white">
                {status}
              </dd>
            </motion.dl>
          </div>
        </motion.section>
      </div>
    </WideLayout>
  );
};

export default EndOfSeasonHoldingMessage;
